import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isNavItemActive, type NavItem } from './navItems';

interface SidebarNavProps {
  readonly items: readonly NavItem[];
  readonly pathname: string;
  readonly collapsed: boolean;
  /** Keeps Discovery's search params and the `ioc` flag across navigations. */
  readonly getNavTo: (path: string) => string;
}

/**
 * The WORKSPACE section. CT-0 drops the second line each item used to carry —
 * the descriptions survive in the mobile nav, where there is room for them.
 */
export const SidebarNav: React.FC<SidebarNavProps> = ({
  items,
  pathname,
  collapsed,
  getNavTo,
}) => {
  const { t } = useTranslation();

  return (
    <nav className={`shrink-0 ${collapsed ? 'pt-6' : 'pt-[18px]'}`}>
      {!collapsed && (
        <p className="pb-2 pl-3 text-[11px] font-semibold uppercase leading-[normal] tracking-[0.88px] text-[rgba(11,11,11,0.5)] dark:text-white/50">
          {t('app.v11.shell.sections.workspace')}
        </p>
      )}

      <ul className={`flex flex-col ${collapsed ? 'items-center gap-1' : 'gap-[2px]'}`}>
        {items.map(item => {
          const isActive = isNavItemActive(pathname, item.path);
          const Icon = item.icon;
          const label = t(item.label);

          return (
            <li key={item.path}>
              <Link
                to={getNavTo(item.path)}
                aria-current={isActive ? 'page' : undefined}
                aria-label={collapsed ? label : undefined}
                title={collapsed ? label : undefined}
                className={`flex items-center rounded-[10px] text-[14px] font-medium leading-5 transition ${
                  collapsed ? 'h-10 w-10 justify-center' : 'gap-3 px-3 py-2.5'
                } ${
                  isActive
                    ? 'bg-v11-sidebar-active-bg text-v11-sidebar-active-text'
                    : 'text-v11-text-primary hover:bg-v11-surface-hover'
                }`}
              >
                <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                {!collapsed && (
                  <span className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span className="truncate">{label}</span>
                    {item.beta && (
                      <span className="rounded bg-amber-400 px-1 py-px text-[9px] font-bold uppercase leading-3 tracking-wider text-v11-bg">
                        {t('app.v11.nav.beta')}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
